import { useHead, useRequestURL, useRoute, useSeoMeta } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from '~/scopes/campaign/composables/useCampaignSeo.translations'
import backMarket from '~/scopes/campaign/const/back-market.translations'

// 1200×630 is the frame every crawler crops to. The path is written out rather than
// built from CAMPAIGN_SLUG because that is how the rest of the scope addresses its
// assets since they moved under `public/mine-my-phone/`.
const OG_IMAGE = {
  src: '/mine-my-phone/raw_og-image.jpg',
  width: 1200,
  height: 630,
}

/**
 * The campaign's `<head>` — one card for the whole experience. Every URL gets the
 * same title, description and image, so nothing here reads the route except the
 * locale and the canonical.
 *
 * A composable rather than a page because there are no pages: all eight routes mount
 * `CampaignHome.vue`, and every panel is a URL state.
 */
export function useCampaignSeo() {
  const i18n = useI18n()
  const route = useRoute()
  const { origin } = useRequestURL()

  // Reactive, unlike `useI18nLocale()` — that reads a module-level ref once, and
  // this component stays mounted across a locale switch.
  const locale = computed(() => {
    const param = route.params.locale

    return Array.isArray(param) ? param[0] : param
  })

  // Self-referencing, minus the query: `?metal=` `?mine=` `?watch=` `?video=`
  // `?view=` are UI state, and `ShareButton` copies the address bar verbatim, so a
  // shared link carries them wherever it lands.
  const canonical = computed(() => `${origin}${route.path}`)

  const title = computed(() => i18n(translations.title))
  const description = computed(() => i18n(translations.description))
  const imageAlt = computed(() => i18n(translations.imageAlt))
  const siteName = computed(() => i18n(backMarket.label))
  const image = `${origin}${OG_IMAGE.src}`

  useSeoMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonical,
    ogType: 'website',
    ogSiteName: siteName,
    ogImage: image,
    ogImageWidth: OG_IMAGE.width,
    ogImageHeight: OG_IMAGE.height,
    ogImageAlt: imageAlt,
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: image,
    twitterImageAlt: imageAlt,
  })

  useHead({
    htmlAttrs: { lang: locale },
    link: [{ rel: 'canonical', href: canonical }],
  })
}
